import { Link, useParams } from "react-router-dom";
import { PATTERNS, t } from "../i18n";
import ProductCard from "../components/ProductCard";
import Faq from "../components/Faq";
import Seo, { JsonLd } from "../components/Seo";
import SameDayCountdown from "../components/SameDayCountdown";

const Reviews = ({ reviews = [], locale }) =>
  reviews.length ? (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {reviews.map((r) => (
        <blockquote key={r.id} className="bg-plaster-deep border border-brass-light p-6" data-testid={`review-${r.id}`}>
          <p className="smallcaps text-brass">
            {t(locale, "sentFrom")} {r.buyer_city || r.buyer_country} {t(locale, "to")} {r.delivery_city}
          </p>
          <p className="mt-3 text-sm leading-relaxed">{r.body}</p>
        </blockquote>
      ))}
    </div>
  ) : null;

const Grid = ({ products, locale }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 stagger">
    {products?.map((p, i) => (
      <ProductCard key={p.id} product={p} locale={locale} index={i} />
    ))}
  </div>
);


const LOCAL_T = {
  en: { neigh: "Neighbourhoods we deliver to", occ: "How people celebrate here", tips: "Sending from abroad? Three tips" },
  fr: { neigh: "Quartiers desservis", occ: "Les occasions ici", tips: "Vous envoyez depuis l'étranger ? Trois conseils" },
  es: { neigh: "Barrios a los que entregamos", occ: "Cómo se celebra aquí", tips: "¿Envías desde el extranjero? Tres consejos" },
  ar: { neigh: "الأحياء التي نوصل إليها", occ: "كيف يحتفل الناس هنا", tips: "ترسل من الخارج؟ ثلاث نصائح" },
  it: { neigh: "I quartieri in cui consegniamo", occ: "Come si festeggia qui", tips: "Invii dall'estero? Tre consigli" },
  nl: { neigh: "Wijken waar we bezorgen", occ: "Hoe mensen hier vieren", tips: "Versturen vanuit het buitenland? Drie tips" },
  de: { neigh: "Stadtteile, in die wir liefern", occ: "So wird hier gefeiert", tips: "Versand aus dem Ausland? Drei Tipps" },
};

// Unique, locally-grounded copy generated per city (Admin → Catalogue → City pages).
const CityLocal = ({ local, locale }) => {
  const lt = LOCAL_T[locale] || LOCAL_T.en;
  return (
    <section className="grid lg:grid-cols-3 gap-10" data-testid="city-local">
      <div className="lg:col-span-2 space-y-8">
        {local.neighbourhoods ? <div><h2 className="font-display text-2xl text-ink mb-3">{lt.neigh}</h2><p className="text-base leading-relaxed text-charcoal">{local.neighbourhoods}</p></div> : null}
        {local.occasions_local ? <div><h2 className="font-display text-2xl text-ink mb-3">{lt.occ}</h2><p className="text-base leading-relaxed text-charcoal">{local.occasions_local}</p></div> : null}
      </div>
      {local.tips?.length ? (
        <aside className="card p-6 self-start">
          <h2 className="smallcaps mb-4">{lt.tips}</h2>
          <ol className="space-y-3 text-sm text-charcoal list-decimal ps-4">{local.tips.map((x, i) => <li key={i}>{x}</li>)}</ol>
        </aside>
      ) : null}
    </section>
  );
};

export const CityView = ({ data, sameDay }) => {
  const { locale = "en" } = useParams();
  const pat = PATTERNS[locale];
  const cityFaq = [...(data.faq || []), ...(data.citation_facts || [])].filter(
    (f, i, arr) => f && f.q && arr.findIndex((x) => x.q === f.q) === i
  );
  return (
    <div data-testid="city-page">
      <Seo title={data.seo?.title} description={data.seo?.description} noindex={!!data.noindex} path={`/${locale}/${pat.city}/${data.slug}`}
           alternates={data.slugs ? Object.fromEntries(Object.keys(PATTERNS).map((l) => [l, `/${l}/${PATTERNS[l].city}/${data.slugs[l]}`])) : null} />
      <JsonLd data={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: cityFaq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }} />
      <section className="relative">
        <img src={data.hero_image_url} alt={data.name} className="w-full h-[380px] object-cover" />
        <div className="absolute inset-0 overlay-hero flex items-end">
          <div className="max-w-content mx-auto w-full px-6 pb-10">
            <h1 className="font-display text-4xl lg:text-5xl text-plaster">
              {sameDay ? `${t(locale, "sameDay")} — ` : ""}
              {{ en: `Flower delivery in ${data.name}`, fr: `Livraison de fleurs à ${data.name}`, es: `Entrega de flores en ${data.name}`, ar: `توصيل الورود في ${data.name}`, it: `Consegna fiori a ${data.name}`, nl: `Bloemen bezorgen in ${data.name}`, de: `Blumenlieferung in ${data.name}` }[locale]}
            </h1>
            <p className="text-plaster opacity-85 mt-3 text-sm">
              {data.zones?.length} {t(locale, "zonesLabel")} · <SameDayCountdown sd={data.same_day} timing={data.timing} locale={locale} city={data.name} />
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-content mx-auto px-6 py-12 lg:py-20 space-y-14 lg:space-y-24">
        <section className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {data.local?.headline ? <h2 className="font-display text-2xl lg:text-3xl text-ink" data-testid="city-local-headline">{data.local.headline}</h2> : null}
            <p className="text-base leading-relaxed text-charcoal whitespace-pre-line" data-testid="city-intro">{data.local?.intro || data.intro_copy}</p>
          </div>
          <div className="card p-6">
            <h2 className="smallcaps mb-4">{t(locale, "zone")}</h2>
            <ul className="space-y-2 text-sm">
              {data.zones?.map((z) => (
                <li key={z.id} className="flex justify-between gap-4 border-b border-brass-light/60 pb-1">
                  <span>{z.name}</span>
                  <span className="tabular text-muted" data-testid={`city-zone-fee-${z.id}`}>{z.fee} {data.currency || "EUR"}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {data.deals?.length ? (
            <section className="space-y-4" data-testid="deals-row">
              <div className="flex items-end justify-between gap-4">
                <h2 className="font-display text-3xl text-ink">{t(locale, "dealsTitle")}</h2>
                <p className="text-sm text-muted">{t(locale, "dealsNote")}</p>
              </div>
              <div className="flex gap-5 overflow-x-auto snap-x-strip pb-2">
              {data.deals.map((p, i) => (
                  <div key={p.id} className="min-w-[220px] max-w-[240px]">
                    <ProductCard product={p} locale={locale} index={i} />
                  </div>
                ))}
              </div>
            </section>
          ) : null}

        <section>
          <h2 className="font-display text-2xl lg:text-3xl text-ink mb-8">{t(locale, "bestSellers")}</h2>
          <Grid products={data.products} locale={locale} />
        </section>

        <section>
          <h2 className="font-display text-2xl text-ink mb-6">{t(locale, "occasions")}</h2>
          <div className="flex flex-wrap gap-2">
            {data.occasions?.map((o) => (
              <Link key={o.id} to={`/${locale}/${pat.combo(o.slug, data.slug)}`} data-testid={`city-occasion-link-${o.key}`} className="text-xs border border-petal text-petal px-3 py-1.5 hover:bg-petal hover:text-plaster transition-colors duration-200">
                {o.name}
              </Link>
            ))}
          </div>
        </section>

        <section className="hidden" aria-hidden="true">
          {/* Florist identities are private to the operations team — not shown to buyers */}
        </section>

        {data.local ? <CityLocal local={data.local} locale={locale} /> : null}
        <Reviews reviews={data.reviews} locale={locale} />
        <Faq items={cityFaq} title={t(locale, "faq")} testid="city-faq" />
      </div>
    </div>
  );
};

export const OccasionView = ({ data }) => {
  const { locale = "en" } = useParams();
  const pat = PATTERNS[locale];
  return (
    <div data-testid="occasion-page">
      <Seo title={`${data.name} | Fleurinelle`} description={data.description} path={`/${locale}/${pat.occasion}/${data.slug}`}
           alternates={data.slugs ? Object.fromEntries(Object.keys(PATTERNS).map((l) => [l, `/${l}/${PATTERNS[l].occasion}/${data.slugs[l]}`])) : null} />
      <section className="relative">
        <img src={data.hero_image_url} alt={data.name} className="w-full h-[340px] object-cover" />
        <div className="absolute inset-0 overlay-hero flex items-end">
          <div className="max-w-content mx-auto w-full px-6 pb-10">
            <h1 className="font-display text-4xl lg:text-5xl text-plaster">{data.name}</h1>
            <p className="text-plaster opacity-85 mt-3 max-w-2xl text-sm">{data.description}</p>
          </div>
        </div>
      </section>
      <div className="max-w-content mx-auto px-6 py-12 lg:py-20 space-y-14 lg:space-y-24">
        <section>
          <div className="flex flex-wrap gap-2 mb-8">
            {data.cities?.map((c) => (
              <Link key={c.id} to={`/${locale}/${pat.combo(data.slug, c.slug)}`} data-testid={`occasion-city-${c.key}`} className="btn-ghost text-xs py-2 px-4">
                {c.name}
              </Link>
            ))}
          </div>
          <Grid products={data.products} locale={locale} />
        </section>
        <section className="grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display text-2xl text-ink mb-4">{t(locale, "suggestions")}</h2>
            <ul className="space-y-3 text-sm">
              {data.card_messages?.map((m) => (
                <li key={m} className="border-s-2 border-brass ps-4 text-charcoal">{m}</li>
              ))}
            </ul>
          </div>
          <div className="card p-8">
            <h2 className="font-display text-2xl text-ink mb-4">{t(locale, "occasionEtiquette")}</h2>
            <p className="text-sm leading-relaxed text-charcoal">{data.etiquette}</p>
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl text-ink mb-6">{t(locale, "occasions")}</h2>
          <div className="flex flex-wrap gap-2">
            {data.related?.map((o) => (
              <Link key={o.id} to={`/${locale}/${pat.occasion}/${o.slug}`} className="text-xs border border-petal text-petal px-3 py-1.5">{o.name}</Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export const ComboView = ({ data }) => {
  const { locale = "en" } = useParams();
  const pat = PATTERNS[locale];
  return (
    <div data-testid="city-occasion-page">
      <Seo title={data.seo?.title} description={data.seo?.description} noindex={!!data.noindex} path={`/${locale}/${pat.combo(data.occasion.slug, data.city.slug)}`}
           alternates={data.city.slugs && data.occasion.slugs ? Object.fromEntries(Object.keys(PATTERNS).map((l) => [l, `/${l}/${PATTERNS[l].combo(data.occasion.slugs[l], data.city.slugs[l])}`])) : null} />
      <JsonLd data={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: (data.faq || []).map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }} />
      <div className="max-w-content mx-auto px-6 py-12 lg:py-20 space-y-14 lg:space-y-24">
        <section className="grid lg:grid-cols-3 gap-10 items-start">
          <div className="lg:col-span-2">
            <nav className="smallcaps mb-4">
              <Link to={`/${locale}`}>Fleurinelle</Link> · <Link to={`/${locale}/${pat.city}/${data.city.slug}`}>{data.city.name}</Link> · {data.occasion.name}
            </nav>
            <h1 data-testid="combo-h1" className="font-display text-4xl lg:text-5xl text-ink">{data.h1}</h1>
            <p className="mt-6 text-base leading-relaxed text-charcoal">{data.intro}</p>
            <p className="mt-6 text-sm text-brass">
              <SameDayCountdown sd={data.same_day} timing={data.timing} locale={locale} city={data.city.name} />
            </p>
          </div>
          <ul className="card p-6 space-y-3 text-sm">
            {data.facts?.map((f) => (
              <li key={f} className="border-b border-brass-light/60 pb-3 last:border-0">{f}</li>
            ))}
          </ul>
        </section>
        <section>
          <Grid products={data.products} locale={locale} />
        </section>
        {data.local ? (
          <section className="max-w-3xl" data-testid="combo-local">
            <p className="text-base leading-relaxed text-charcoal">{data.local}</p>
          </section>
        ) : null}
        <Reviews reviews={data.reviews} locale={locale} />
        <Faq items={data.faq} title={t(locale, "faq")} testid="combo-faq" />
      </div>
    </div>
  );
};

export const CorridorView = ({ data }) => {
  const { locale = "en" } = useParams();
  const pat = PATTERNS[locale];
  return (
    <div data-testid="corridor-page">
      <Seo title={data.seo?.title} description={data.seo?.description} path={`/${locale}/${pat.corridor(data.country_slug)}`} alternates={Object.fromEntries(Object.keys(PATTERNS).map(l => [l, `/${l}/${PATTERNS[l].corridor(data.country_slug)}`]))} />
      {(data.json_ld || []).map((d, i) => <JsonLd key={i} data={d} />)}
      <div className="max-w-content mx-auto px-6 py-12 lg:py-20 space-y-14 lg:space-y-24">
        <section className="max-w-3xl">
          <h1 className="font-display text-4xl lg:text-5xl text-ink">{data.h1}</h1>
          <p className="mt-6 text-base leading-relaxed text-charcoal">{data.intro}</p>
          <dl className="mt-8 grid sm:grid-cols-3 gap-6 text-sm">
            <div><dt className="smallcaps">{t(locale, "orderBefore")}</dt><dd className="tabular text-ink mt-1">{data.local_cutoff}</dd></div>
            <div><dt className="smallcaps">{t(locale, "currency")}</dt><dd className="text-ink mt-1">{data.currency}</dd></div>
            <div><dt className="smallcaps">{t(locale, "paymentMethods")}</dt><dd className="text-ink mt-1">{data.payment_methods?.join(", ")}</dd></div>
          </dl>
        </section>
        <section>
          <h2 className="font-display text-2xl text-ink mb-6">{t(locale, "corridorCities")}</h2>
          <div className="flex flex-wrap gap-2">
            {data.cities?.map((c) => (
              <Link key={c.id} to={`/${locale}/${pat.city}/${c.slug}`} className="btn-ghost text-xs py-2 px-4" data-testid={`corridor-city-${c.key}`}>{c.name}</Link>
            ))}
          </div>
        </section>
        <section><Grid products={data.products} locale={locale} /></section>
        <Reviews reviews={data.reviews} locale={locale} />
        <Faq items={data.faq} title={t(locale, "faq")} testid="corridor-faq" />
      </div>
    </div>
  );
};

export const FloristView = () => {
  const { locale = "en" } = useParams();
  // Florist profile pages are no longer exposed — Fleurinelle is the seller.
  return (
    <div data-testid="florist-page" className="max-w-content mx-auto px-6 py-24 text-center">
      <Seo title="Fleurinelle" noindex />
      <h1 className="font-display text-3xl text-ink mb-4">Fleurinelle</h1>
      <p className="text-sm text-muted">
        <Link className="underline" to={`/${locale}`}>{t(locale, "backHome") || "Return to Fleurinelle"}</Link>
      </p>
    </div>
  );
};

export const GuideView = ({ data }) => {
  const { locale = "en" } = useParams();
  return (
    <article data-testid="guide-page" className="max-w-content mx-auto px-6 py-12 lg:py-20">
      <Seo title={`${data.title} | Fleurinelle`} description={data.excerpt} path={`/${locale}/${(PATTERNS[locale] || PATTERNS.en).guide}/${data.slug}`}
           alternates={data.slug ? Object.fromEntries(Object.keys(PATTERNS).map((l) => [l, `/${l}/${PATTERNS[l].guide}/${data.slug}`])) : null} type="article" />
      <JsonLd data={{ "@context": "https://schema.org", "@type": "Article", headline: (data.title || "").slice(0, 110),
                      description: data.excerpt, inLanguage: locale, image: data.hero_image_url ? [data.hero_image_url] : undefined,
                      datePublished: data.published_at, author: { "@type": "Organization", name: "Fleurinelle" },
                      publisher: { "@type": "Organization", name: "Fleurinelle" } }} />
      <JsonLd data={{ "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Fleurinelle", item: `${window.location.origin}/${locale}` },
        { "@type": "ListItem", position: 2, name: t(locale, "guides"), item: `${window.location.origin}/${locale}/guides` },
        { "@type": "ListItem", position: 3, name: data.title }] }} />
      <img src={data.hero_image_url} alt={data.title} className="w-full h-[360px] object-cover" />
      <h1 className="font-display text-3xl lg:text-4xl text-ink mt-10 max-w-3xl">{data.title}</h1>
      <div className="mt-8 max-w-2xl text-base leading-relaxed whitespace-pre-line text-charcoal">{data.body}</div>
    </article>
  );
};
